// Countries and states/provinces sourced from country-state-city (full ISO 3166-1/-2 list).
// COUNTRIES: full list of 250 countries, sorted alphabetically by Spanish name (México first to
// keep the primary market on top). STATES_BY_COUNTRY: dictionary of country code → string[] of
// state/province names — populated lazily when accessed.
import { Country, State } from "country-state-city";

// --- Spanish overrides for the most common Latin-American/EU country names ---
// (country-state-city ships English names by default).
const ES_NAMES = {
  MX: "México",
  AR: "Argentina",
  ES: "España",
  US: "Estados Unidos",
  CL: "Chile",
  CO: "Colombia",
  PE: "Perú",
  UY: "Uruguay",
  BR: "Brasil",
  EC: "Ecuador",
  VE: "Venezuela",
  GT: "Guatemala",
  SV: "El Salvador",
  HN: "Honduras",
  NI: "Nicaragua",
  CR: "Costa Rica",
  PA: "Panamá",
  PR: "Puerto Rico",
  DO: "República Dominicana",
  CU: "Cuba",
  BO: "Bolivia",
  PY: "Paraguay",
  CA: "Canadá",
  GB: "Reino Unido",
  FR: "Francia",
  DE: "Alemania",
  IT: "Italia",
  PT: "Portugal",
  NL: "Países Bajos",
  BE: "Bélgica",
  CH: "Suiza",
  AT: "Austria",
  SE: "Suecia",
  NO: "Noruega",
  DK: "Dinamarca",
  FI: "Finlandia",
  IE: "Irlanda",
  PL: "Polonia",
  CZ: "Chequia",
  GR: "Grecia",
  RO: "Rumania",
  HU: "Hungría",
  TR: "Turquía",
  RU: "Rusia",
  UA: "Ucrania",
  JP: "Japón",
  CN: "China",
  KR: "Corea del Sur",
  IN: "India",
  AU: "Australia",
  NZ: "Nueva Zelanda",
  ZA: "Sudáfrica",
  EG: "Egipto",
  MA: "Marruecos",
  SA: "Arabia Saudita",
  AE: "Emiratos Árabes Unidos",
  IL: "Israel",
  IS: "Islandia",
  PH: "Filipinas",
  ID: "Indonesia",
  TH: "Tailandia",
  VN: "Vietnam",
  MY: "Malasia",
  SG: "Singapur",
};

const COLLATOR = new Intl.Collator("es", { sensitivity: "base" });

// Build the full country list once.
const rawCountries = Country.getAllCountries().map((c) => ({
  code: c.isoCode,
  name: ES_NAMES[c.isoCode] || c.name,
  flag: c.flag || c.isoCode,
  phonecode: c.phonecode,
}));
// Sort alphabetically in Spanish, then float the primary market (MX) to the top.
rawCountries.sort((a, b) => COLLATOR.compare(a.name, b.name));
const mxIdx = rawCountries.findIndex((c) => c.code === "MX");
if (mxIdx > 0) {
  const [mx] = rawCountries.splice(mxIdx, 1);
  rawCountries.unshift(mx);
}
// Append a generic "Otro" so users can opt-out.
rawCountries.push({ code: "OTHER", name: "Otro", flag: "" });

export const COUNTRIES = rawCountries;

// Lazy state lookup — `STATES_BY_COUNTRY['MX']` returns the precomputed array on first access.
const _statesCache = {};
function statesFor(code) {
  if (!code) return ["Otro"];
  if (code === "OTHER") return ["Otro"];
  if (_statesCache[code]) return _statesCache[code];
  const list = State.getStatesOfCountry(code) || [];
  const names = list.map((s) => s.name);
  names.sort((a, b) => COLLATOR.compare(a, b));
  if (!names.length) names.push("Otro");
  else names.push("Otro");
  _statesCache[code] = names;
  return names;
}

export const STATES_BY_COUNTRY = new Proxy(
  {},
  {
    get: (_, key) => statesFor(typeof key === "string" ? key.toUpperCase() : key),
    has: () => true,
    // Make Object.keys / for…in iterate all known country codes (fixes
    // legacy consumers like UserManagement.jsx that rely on Object.keys).
    ownKeys: () => COUNTRIES.map((c) => c.code),
    getOwnPropertyDescriptor: () => ({ enumerable: true, configurable: true }),
  },
);

export function countryName(code) {
  if (!code) return "";
  const c = COUNTRIES.find((x) => x.code === code.toUpperCase());
  return c ? c.name : code;
}

export const CATEGORIES = [
  { value: "open", label: "Open", short: "Open", description: "Sin limite de nivel" },
  { value: "A", label: "A — Avanzado", short: "A", description: "Nivel A · Avanzado / Elite" },
  { value: "B", label: "B — Intermedio Alto", short: "B", description: "Nivel B · Intermedio alto" },
  { value: "C", label: "C — Intermedio", short: "C", description: "Nivel C · Intermedio" },
  { value: "D", label: "D — Principiante", short: "D", description: "Nivel D · Principiante" },
  { value: "1", label: "Primera", short: "1ra", description: "Nivel competitivo alto" },
  { value: "2", label: "Segunda", short: "2da", description: "Competitivo" },
  { value: "3", label: "Tercera", short: "3ra", description: "Intermedio alto" },
  { value: "4", label: "Cuarta", short: "4ta", description: "Intermedio" },
  { value: "5", label: "Quinta", short: "5ta", description: "Intermedio bajo" },
  { value: "6", label: "Sexta", short: "6ta", description: "Principiante" },
];

export function categoryLabel(value) {
  const c = CATEGORIES.find((x) => x.value === value);
  return c ? c.label : value;
}

export function categoryShort(value) {
  const c = CATEGORIES.find((x) => x.value === value);
  return c ? c.short : value;
}
